/** @module One owned copy up close: value, purchase, condition, language, note, history, archive. */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type components, problemFrom } from "@/api/client";
import { CardFace } from "@/components/card-face";
import { LocationSelect } from "@/components/location-select";
import { NoteInput } from "@/components/suggested-inputs";
import { AmountInput, draftOf, PurchaseFields, purchaseBody } from "@/components/purchase-fields";
import { Sheet } from "@/components/ui/overlays";
import { printedNumber, variantLabel } from "@/lib/cards";
import { ARCHIVE_REASONS, type Condition, CONDITIONS, LANGUAGES } from "@/lib/collection";
import { day, money, parseAmount, signedMoney } from "@/lib/format";
import { useAuthedImage } from "@/lib/images";

export type Item = components["schemas"]["CollectionItemDetailOut"];

async function fetchEvents(itemId: string) {
  const { data, response } = await api.GET("/api/v1/collection/items/{item_id}/events", {
    params: { path: { item_id: itemId } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

/** The rectified photo of the scan that booked this copy. */
function ScanPhoto({ scanId }: { scanId: string }) {
  const { t } = useTranslation();
  const url = useAuthedImage(`/api/v1/scans/${scanId}/crop?size=large`);
  if (!url) return null;
  return (
    <img src={url} alt={t("item.scanPhoto")} className="card-shape w-14 shrink-0 object-cover" />
  );
}

/** What happened to the copy, newest first. */
export function ItemHistory({ item }: { item: Item }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const events = useQuery({
    queryKey: ["item-events", item.id],
    queryFn: () => fetchEvents(item.id),
  });
  return (
    <div className="flex flex-col gap-[6px]">
      <span className="t-label">{t("item.history")}</span>
      {item.scan_id ? (
        <div className="row gap-[10px]">
          <ScanPhoto scanId={item.scan_id} />
          <span className="t-sm t-muted">
            {t("item.scannedOn", { date: day(item.created_at, locale) })}
          </span>
        </div>
      ) : null}
      {(events.data ?? []).map((event) => (
        <div key={event.id} className="row-between t-sm">
          <span>{t(`item.event.${event.type}`, event.type)}</span>
          <span className="num t-muted">{day(event.occurred_at, locale)}</span>
        </div>
      ))}
    </div>
  );
}

export function ItemSheet({
  item,
  onClose,
  onChanged,
  showCardLink = true,
}: {
  item: Item;
  onClose: () => void;
  onChanged: () => void;
  /** Off on the card page itself, where the link would lead back to the same screen. */
  showCardLink?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const [condition, setCondition] = useState(item.condition as Condition);
  const [language, setLanguage] = useState(item.language);
  const [note, setNote] = useState(item.note ?? "");
  const [purchase, setPurchase] = useState(() => draftOf(item, locale));
  const [location, setLocation] = useState<string | null>(item.location?.id ?? null);
  const [archiving, setArchiving] = useState(false);
  const [salePrice, setSalePrice] = useState("");
  const purchaseFields = purchaseBody(purchase);
  const sale = parseAmount(salePrice);

  const save = useMutation({
    mutationFn: async () => {
      const { data, response } = await api.PATCH("/api/v1/collection/items/{item_id}", {
        params: { path: { item_id: item.id } },
        body: {
          condition,
          language,
          note: note.trim() || null,
          location_id: location,
          ...purchaseFields,
        },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: onChanged,
  });

  const archive = useMutation({
    mutationFn: async (reason: string | null) => {
      const { data, response } =
        reason !== null
          ? await api.POST("/api/v1/collection/items/{item_id}/archive", {
              params: { path: { item_id: item.id } },
              body: {
                status: reason as (typeof ARCHIVE_REASONS)[number],
                amount: sale ?? undefined,
              },
            })
          : await api.POST("/api/v1/collection/items/{item_id}/restore", {
              params: { path: { item_id: item.id } },
            });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: onChanged,
  });

  const busy = save.isPending || archive.isPending;
  const gain = item.gain === null || item.gain === undefined ? null : Number(item.gain);
  const card = item.card;
  const name = card?.name ?? item.print_id;

  return (
    <Sheet open onClose={onClose} label={name}>
      <div className="flex flex-col gap-5">
        <div className="flex gap-4">
          <span className="card-shape block w-24 shrink-0">
            <CardFace printId={item.print_id} name={name} />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <h2 className="t-h3">{name}</h2>
            <span className="t-sm t-muted num">
              {card ? printedNumber(card) : item.print_id}
              {card
                ? ` · ${variantLabel(card.variant_key, (key, fallback) => t(key, fallback))}`
                : ""}
            </span>
            <span className="num-xl mt-1 text-[24px]">
              {item.price?.suspicious
                ? t("collection.suspicious")
                : (money(item.value, locale) ?? t("collection.unvalued"))}
            </span>
            {gain !== null ? (
              <span className={`t-sm num ${gain >= 0 ? "gain" : "loss"}`}>
                {signedMoney(item.gain, locale)} {t("purchase.sinceBuy")}
              </span>
            ) : null}
            {item.sale_price ? (
              <span className="t-xs t-muted">
                {t("purchase.soldFor", { price: money(item.sale_price, locale) })}
              </span>
            ) : null}
            {item.price?.value_field ? (
              <span className="t-xs t-muted">
                {t("collection.priceFrom", {
                  field: item.price.value_field,
                  price: money(item.price.value, locale) ?? "–",
                })}
              </span>
            ) : null}
            {showCardLink ? (
              <Link to="/cards/$printId" params={{ printId: item.print_id }} className="t-sm">
                {t("collection.cardDetails")}
              </Link>
            ) : null}
          </div>
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className="t-label pb-2">{t("collection.condition")}</legend>
          <div className="flex flex-wrap gap-2">
            {CONDITIONS.map((option) => (
              <button
                key={option}
                type="button"
                className="chip num"
                aria-pressed={condition === option}
                onClick={() => {
                  setCondition(option);
                }}
              >
                {option}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-2">
          <legend className="t-label pb-2">{t("collection.language")}</legend>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((option) => (
              <button
                key={option}
                type="button"
                className="chip"
                aria-pressed={language === option}
                onClick={() => {
                  setLanguage(option);
                }}
              >
                {option.toUpperCase()}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-2">
          <legend className="t-label pb-2">{t("purchase.title")}</legend>
          <PurchaseFields draft={purchase} onChange={setPurchase} />
        </fieldset>

        <NoteInput value={note} onChange={setNote} />

        <LocationSelect value={location} onChange={setLocation} />

        <ItemHistory item={item} />

        {archiving ? (
          <div className="flex flex-col gap-3">
            <AmountInput
              label={t("purchase.salePrice")}
              value={salePrice}
              onChange={setSalePrice}
            />
            <span className="t-label">{t("collection.bulk.why")}</span>
            <div className="flex flex-wrap gap-2">
              {ARCHIVE_REASONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  className="chip"
                  disabled={busy || sale === undefined}
                  onClick={() => {
                    archive.mutate(reason);
                  }}
                >
                  {t(`collection.reason.${reason}`)}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {save.isError || archive.isError ? (
          <p role="alert" className="t-sm loss">
            {t("common.saveError")}
          </p>
        ) : null}

        <div className="row gap-2">
          <button
            type="button"
            disabled={busy || purchaseFields === undefined}
            onClick={() => {
              save.mutate();
            }}
            className="btn btn-primary grow"
          >
            {t("common.save")}
          </button>
          {item.status === "active" ? (
            <button
              type="button"
              disabled={busy}
              aria-pressed={archiving}
              onClick={() => {
                setArchiving((current) => !current);
              }}
              className="btn btn-danger"
            >
              {t("collection.archive")}
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                archive.mutate(null);
              }}
              className="btn btn-secondary"
            >
              {t("collection.restore")}
            </button>
          )}
          <button type="button" onClick={onClose} className="btn btn-ghost">
            {t("common.close")}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
